// PageDescription: 실시간 의료 상담 상세 페이지
import { dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import QnaDetailMobileTemplate from '@/components/templates/mobile/communication/Qna/QnaDetailMobileTemplate';
import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';
import useGetQnaDetailWithNearSimplyQuestions from '@/hooks/qna/useGetQnaDetailWithNearSimplyQuestions';
import useGetRelatedQna from '@/hooks/qna/useRelatedQna';
import useCustomRouter from '@/hooks/useCustomRouter';
import { ExtendedQueryClient } from '@/models/common/query-client';
import { ssrQueryCache } from '@/models/common/ssr-cache';
import { CustomPageProps } from '@/types/app';
import { QuestionAccessibleType } from '@/types/bff/gql-schema';
import { GraphqlError, ServerError } from '@/types/error';

interface Props extends CustomPageProps {
  id: string;
}

const QnaDetail = ({ id, client }: Props) => {
  const { router } = useCustomRouter();
  return <QnaDetailMobileTemplate questionPid={id} client={client} missionId={router.query.missionId as string} />;
};

QnaDetail.Layout = DefaultLayout;

export default QnaDetail;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const id = ctx.params?.id as string;

  if (!id) {
    return {
      redirect: {
        destination: '/content/qna',
        permanent: false,
      },
    };
  }

  const queryClient = new ExtendedQueryClient();

  const cacheKey = ctx.req.url as string;

  try {
    const questionDetail = await useGetQnaDetailWithNearSimplyQuestions.fetchQuery(queryClient, id);

    if (questionDetail.questionV2?.accessibleType !== QuestionAccessibleType.All) {
      return {
        props: {
          id,
        },
      };
    }

    if (ssrQueryCache.has(cacheKey)) {
      queryClient.setQueryDataFromCache(ssrQueryCache.get(cacheKey) ?? []);

      return {
        props: {
          id,
          dehydratedState: dehydrate(queryClient),
        },
      };
    }

    await useGetRelatedQna.prefetchQuery(queryClient, id);
  } catch (e: unknown) {
    const { code } = e as unknown as GraphqlError;
    if (code === ServerError.NotFound) {
      return {
        notFound: true,
      };
    }
    if (ctx.res && code === ServerError.BadRequest) {
      ctx.res.statusCode = 400;
    }

    return {
      notFound: true,
    };
  }

  ssrQueryCache.set(cacheKey, queryClient.getQueryClientData([useGetRelatedQna.getKey(id)]), SIX_HOURS_IN_MILLISECONDS);

  return {
    props: {
      id,
      dehydratedState: dehydrate(queryClient),
    },
  };
};
